// src/components/HistoryTimeline.js
import React, { useState, useRef, useEffect } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { timelineData } from './timelineData.js';
import Picture from './components/Picture.jsx';

const HistoryTimeline = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const constraintsRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const updateDimensions = () => {
      if (constraintsRef.current) {
        setContainerWidth(constraintsRef.current.offsetWidth);
      }
      setIsMobile(window.innerWidth < 768);
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const cardWidth = isMobile ? containerWidth : containerWidth / 3;
  const totalWidth = cardWidth * timelineData.length;

  const x = useMotionValue(0);
  const dragX = useTransform(x, (latest) => latest);

  useEffect(() => {
    const unsubscribe = dragX.onChange((latest) => {
      const newIndex = Math.round(-latest / cardWidth);
      if (newIndex !== currentIndex && newIndex >= 0 && newIndex < timelineData.length) {
        setCurrentIndex(newIndex);
      }
    });

    return () => unsubscribe();
  }, [dragX, cardWidth, currentIndex, timelineData.length]);

  return (
    <div className="w-full bg-white py-8 md:py-6  overflow-hidden text-center md:text-left">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 md:mb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
          <div className="font-bold text-4xl md:text-6xl">
            <div className="text-[#FF0000] 	">Gtechniq</div>
            <div className="text-black ml-0 md:ml-20 -py-6 align-top">Nossa História</div>
          </div>
          <p className="text-gray-600 self-end text-sm md:text-base">
            Ao longo de mais de 20 anos, os produtos da Gtechniq foram continuamente aperfeiçoados para oferecer 
            uma linha que supera em brilho e durabilidade a concorrência. A Gtechniq construiu uma marca
             inovadora e autêntica, que se destaca de tudo o que existe no mercado.
          </p>
        </div>
      </div>
      <div 
        className="relative cursor-grab active:cursor-grabbing"
        ref={constraintsRef}
      >
        <motion.div
          drag="x"
          dragConstraints={{ left: -totalWidth + containerWidth, right: 0 }}
          className="flex"
          style={{ 
            width: `${totalWidth}px`,
            x: x
          }}
        >
          {timelineData.map((item) => (
            <div key={item.year} style={{ width: `${cardWidth}px` }} className="flex-shrink-0 px-2">
              <div className="relative aspect-[3/4] group">
                <Picture
                  src={item.image}
                  alt={`Gtechniq history ${item.year}`}
                  className="w-full h-full object-cover rounded-lg shadow-md group-hover:blur-[5px] transition-all duration-300"
                />
                <div className="absolute inset-0 from-black bg-gradient-45  transition-all duration-300 rounded-lg flex flex-col justify-between p-4">
                  <div className="inline-block mb-4 relative">
                    <div className="absolute">
                      <span className="bg-[#ff0000] text-white font-semibold px-4 py-1 inline-block relative z-10">
                        {item.title}
                      </span>
                      <span
                        className="absolute top-0 right-0 bottom-0 bg-[#ff0000] transform -skew-x-12"
                        style={{
                          width: '100%',
                          transformOrigin: 'bottom right'
                        }}
                      ></span>
                    </div>
                  </div>
                  <div className="text-white">
                    <h3 className="text-2xl md:text-4xl font-bold mb-2">{item.year}</h3>
                    <div className="h-24 md:h-32 overflow-y-auto">
                      <p className="text-xs md:text-sm">{item.content}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
      <div className="flex justify-center mt-4 md:mt-8">
        {timelineData.map((_, index) => (
          <button
            key={index}
            className={`h-2 w-2 md:h-3 md:w-3 rounded-full mx-1 ${
              index === currentIndex ? 'bg-[#FF0000]' : 'bg-gray-300'
            }`}
            onClick={() => x.set(-index * cardWidth)}
          />
        ))}
      </div>
    </div>
  );
};

export default HistoryTimeline;
